import { create } from 'zustand';
import {
  DEFAULT_GATEWAY_ID,
  type GatewayId,
  type VerseFlavor,
  findGateway,
  gatewayIdForVerseEnv,
  resolveVerseEntry,
} from '@rb/shared/admin-verses';
import type { VerseConnectionManager } from './VerseConnectionManager';

/** localStorage key persisting the manual gateway override (absent = derive from verse env). */
export const GATEWAY_STORAGE_KEY = 'rb-admin-app:gateway';

interface ActiveVerseState {
  manager: VerseConnectionManager | null;
  activeVerseId: string | null;
  /**
   * Manual gateway override. null = derive the gateway from the active verse's env
   * (prod verse → prod gateway, …). Set via the GatewaySelector toggle; cleared
   * whenever the user switches verse.
   */
  gatewayOverride: GatewayId | null;
  switching: boolean;
  /** Last connection error — shown in Layout so user knows why no verse is active. */
  connectionError: string | null;
  setManager: (m: VerseConnectionManager | null) => void;
  setActive: (verseId: string | null) => void;
  setGatewayOverride: (id: GatewayId | null) => void;
  setSwitching: (b: boolean) => void;
  setConnectionError: (msg: string | null) => void;
}

export const useActiveVerseStore = create<ActiveVerseState>((set) => ({
  manager: null,
  activeVerseId: null,
  gatewayOverride: null,
  switching: false,
  connectionError: null,
  setManager: (manager) => set({ manager }),
  setActive: (activeVerseId) => set({ activeVerseId }),
  setGatewayOverride: (gatewayOverride) => set({ gatewayOverride }),
  setSwitching: (switching) => set({ switching }),
  setConnectionError: (connectionError) => set({ connectionError }),
}));

/**
 * 유효 gateway: override 가 있으면 그것, 없으면 활성 verse 의 env 에서 파생.
 * 활성 verse 가 없으면 기본 gateway(prod) 로 fallback.
 */
export function selectEffectiveGatewayId(
  state: Pick<ActiveVerseState, 'gatewayOverride' | 'activeVerseId'>
): GatewayId {
  if (state.gatewayOverride) return state.gatewayOverride;
  if (!state.activeVerseId) return DEFAULT_GATEWAY_ID;
  return gatewayIdForVerseEnv(resolveVerseEntry(state.activeVerseId).env);
}

/** 유효 gateway id 를 반환하는 리액티브 훅. */
export const useEffectiveGatewayId = (): GatewayId => useActiveVerseStore(selectEffectiveGatewayId);

/**
 * 활성 verse 의 flavor(web2/web3). 활성 verse 가 없으면 null.
 *
 * 미등록(수동 입력) verse 는 `resolveVerseEntry` 가 web3 로 합성하므로 web2 전용
 * 화면은 노출되지 않는다 — 알 수 없는 verse 에서 web2 전용 컬렉션을 조회하지 않도록
 * 닫아두는 쪽이 안전하다.
 */
export function selectActiveVerseFlavor(
  state: Pick<ActiveVerseState, 'activeVerseId'>
): VerseFlavor | null {
  if (!state.activeVerseId) return null;
  return resolveVerseEntry(state.activeVerseId).flavor;
}

/** 활성 verse flavor 를 반환하는 리액티브 훅. */
export const useActiveVerseFlavor = (): VerseFlavor | null =>
  useActiveVerseStore(selectActiveVerseFlavor);

/**
 * 초기 로드 시 raw gateway 값(?gateway= 쿼리 또는 저장된 값)을 override 로 채택할지 결정.
 * 유효한 gateway 이고 verse env 기본값과 다를 때만 override, 그 외엔 null(=verse env 파생).
 */
export function resolveInitialGatewayOverride(
  rawGateway: string | null,
  verseGateway: GatewayId
): GatewayId | null {
  if (rawGateway && findGateway(rawGateway) && rawGateway !== verseGateway) {
    return rawGateway as GatewayId;
  }
  return null;
}
